import { groupBy } from 'lodash';
import type { Observable } from 'rxjs';
import { catchError, first, from, map, of } from 'rxjs';

import { FerryEndpoint } from '@apps/main/modules/api/ferry';
import { FlightEndpoint } from '@apps/main/modules/api/flight';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type {
  AutoCompleteSuggestion,
  AutoCompleteSuggestionsByCategory,
  Coordinate,
  DestinationAutoCompleteSuggestion,
  FerryAutoCompleteSuggestion,
  FlightAutoCompleteSuggestion,
  LocationAutoCompleteSuggestion,
  SearchSuggestionCriteria,
} from '@libs/trip-planner-interfaces';
import {
  AutoCompleteSearchType,
  CategoryType,
  isPositionSuggestionCriteria,
} from '@libs/trip-planner-interfaces';
import { assertValue, exhaustiveGuard } from '@libs/utils';
import { AutoCompleteService } from '@libs/maps';

import { ContentApi } from '../api/content/contentApi';
import { mapLanguageLocale } from '../localization/localeUtils';

const { t } = i18n.global;

interface SuggestionSearchService {
  search: (
    searchSuggestionCriteria: SearchSuggestionCriteria
  ) => Observable<AutoCompleteSuggestionsByCategory[]>;
}

export function groupSuggestions(
  response: AutoCompleteSuggestion[]
): AutoCompleteSuggestionsByCategory[] {
  const grouped = groupBy(response, (suggestion) => suggestion.category);
  return Object.entries(grouped).map(([, suggestions]) => {
    const firstSuggestion = suggestions[0];
    assertValue(
      firstSuggestion,
      'suggestionSearchService.ts > groupSuggestions > firstSuggestion'
    );

    return {
      label: firstSuggestion.category,
      type: firstSuggestion.categoryType,
      data: suggestions,
    };
  });
}

export function useSuggestionSearchService(): SuggestionSearchService {
  const contentApi = new ContentApi();
  const flightEndPoint = new FlightEndpoint();
  const ferryEndpoint = new FerryEndpoint();
  const mapsAutocompleteService = new AutoCompleteService(mapLanguageLocale);

  function search(
    searchSuggestionCriteria: SearchSuggestionCriteria
  ): Observable<AutoCompleteSuggestionsByCategory[]> {
    const getSuggestions = ():
      | Observable<AutoCompleteSuggestion[]>
      | undefined => {
      switch (searchSuggestionCriteria.searchType) {
        case AutoCompleteSearchType.MapLocation: {
          return searchMapLocationSuggestions(
            searchSuggestionCriteria.searchText
          );
        }

        case AutoCompleteSearchType.Destination:
          return searchDestinationSuggestions(
            encodeURIComponent(searchSuggestionCriteria.searchText)
          );

        case AutoCompleteSearchType.Flight:
          return isPositionSuggestionCriteria(searchSuggestionCriteria)
            ? searchAirportSuggestionsByPosition(
                searchSuggestionCriteria.position
              )
            : searchAirportSuggestions(searchSuggestionCriteria.searchText);

        case AutoCompleteSearchType.FerryRoute:
          return searchFerryRoutesSuggestions(
            searchSuggestionCriteria.searchText
          );

        default:
          exhaustiveGuard(
            searchSuggestionCriteria,
            "Invalid AutoCompleteSearchType. Must be either 'mapLocation', 'destination', 'ferryRoute' or 'flight'."
          );
      }
    };

    const result = getSuggestions() ?? of();

    return result.pipe(
      catchError(() => of([])),
      map(groupSuggestions)
    );
  }

  function searchAirportSuggestions(
    searchTerm: string
  ): Observable<FlightAutoCompleteSuggestion[]> {
    return flightEndPoint.searchSuggestions(searchTerm);
  }

  function searchAirportSuggestionsByPosition(
    position: Coordinate
  ): Observable<FlightAutoCompleteSuggestion[]> {
    return flightEndPoint.searchSuggestionsByPosition(position);
  }

  function searchDestinationSuggestions(
    searchTerm: string
  ): Observable<DestinationAutoCompleteSuggestion[]> {
    return contentApi.searchSuggestions(searchTerm);
  }

  function searchFerryRoutesSuggestions(
    searchTerm: string
  ): Observable<FerryAutoCompleteSuggestion[]> {
    return ferryEndpoint.searchFerryRoutes(searchTerm).pipe(
      map((ferryRoutes) =>
        ferryRoutes.map(
          (ferryRoute): FerryAutoCompleteSuggestion => ({
            data: ferryRoute,
            category: '',
            categoryType: CategoryType.FerryRoute,
            label: `${ferryRoute.from.code}-${ferryRoute.to.code}`,
          })
        )
      )
    );
  }

  function searchMapLocationSuggestions(
    input: string
  ): Observable<LocationAutoCompleteSuggestion[]> {
    const mapAutoCompletePredictions = from(
      mapsAutocompleteService.getAutocompleteSuggestions(input)
    );

    return mapAutoCompletePredictions.pipe(
      first(),
      map((mapPredictions) =>
        mapPredictions.map<LocationAutoCompleteSuggestion>((mapPrediction) => ({
          category: t('planner.cities_areas'),
          categoryType: CategoryType.Location,
          label: mapPrediction.description,
          placeId: mapPrediction.id,
        }))
      )
    );
  }

  return {
    search,
  };
}
